<script setup lang="ts">
import { computed } from 'vue'
import { PencilSquareIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { useI18n } from '@/composable/useI18n'

const props = withDefaults(
  defineProps<{
    borderColor: string
    borderWidth?: number
    editMode: boolean
    shadow?: 'sm' | 'md'
  }>(),
  {
    borderWidth: 4,
    shadow: 'sm',
  },
)

const emit = defineEmits<{
  edit: []
  remove: []
}>()

const { t } = useI18n()

const articleClass = computed(() => {
  const shadowClass = props.shadow === 'md' ? 'shadow-md transition-all duration-200' : 'shadow-sm'
  const groupClass = props.editMode ? 'group' : ''
  return `relative flex h-full w-full flex-col overflow-hidden border border-stroke bg-white ${shadowClass} ${groupClass}`
})

const contentClass = computed(() => {
  if (!props.editMode) return 'flex h-full min-h-0 flex-1 flex-col'
  return 'flex min-h-0 flex-1 flex-col transition-all group-hover:blur-[0.5px]'
})
</script>

<template>
  <article :class="articleClass" :style="{ borderTop: `${borderWidth}px solid ${borderColor}` }">
    <div :class="contentClass">
      <slot />
    </div>

    <div
      v-if="editMode"
      class="absolute right-3 top-3 z-20 flex items-center gap-2 opacity-0 transition-all group-hover:opacity-100"
    >
      <button
        type="button"
        class="flex h-5 w-5 items-center justify-center bg-white text-primary shadow-md transition-all hover:bg-primary hover:text-white"
        :title="t('custom_dashboards.view.actions.edit_widget')"
        :aria-label="t('custom_dashboards.view.actions.edit_widget')"
        @click="emit('edit')"
      >
        <PencilSquareIcon class="h-4 w-4" />
      </button>
      <button
        type="button"
        class="flex h-5 w-5 items-center justify-center bg-white text-red shadow-md transition-all hover:bg-red hover:text-white"
        :title="t('custom_dashboards.view.actions.delete_widget')"
        :aria-label="t('custom_dashboards.view.actions.delete_widget')"
        @click="emit('remove')"
      >
        <TrashIcon class="h-4 w-4" />
      </button>
    </div>
  </article>
</template>
